import {calculateErrorMessage} from 'common/helpers'
import {FC, useEffect, useState} from 'react'
import {Button, Image} from 'react-bootstrap'
import Modal from 'react-bootstrap/Modal'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useNavigate, useSearchParams} from 'react-router'
import {toast} from 'react-toastify'
import {verifyStudentParentEmail} from 'setup/redux/Actions/UserSecurityDetails/userSecurityDetails'

const VerifyEmailPage: FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  // Admission applicants (link sent by the /admissions signup) get email
  // wording and go back to their application rather than the school's portal
  // login. Student/parent "verify your information" links keep theirs.
  const isAdmission = searchParams.get('admission') === '1'

  useEffect(() => {
    const token = searchParams.get('token')
    const type = searchParams.get('type')

    if (!token || !type) {
      setStatus('error')
      setMessage(intl.formatMessage({id: 'VERIFICATION.INVALID_LINK'}))
      return
    }

    const onSuccess = (response: any) => {
      setStatus('success')
      const successMsg = isAdmission
        ? intl.formatMessage({id: 'ADMISSION.VERIFY.DONE_BODY'})
        : response?.data?.message || intl.formatMessage({id: 'VERIFICATION.EMAIL_VERIFIED_SUCCESS'})
      setMessage(successMsg)
      toast.success(successMsg)
    }

    const onFailure = (error: any) => {
      setStatus('error')
      const errorMsg = calculateErrorMessage(error)
      setMessage(errorMsg)
      toast.error(errorMsg)
    }

    dispatch(verifyStudentParentEmail({token, type}, onSuccess, onFailure) as any)
  }, [searchParams, intl, dispatch, isAdmission])

  const handleLoginClick = () => {
    navigate(isAdmission ? '/admissions/dashboard' : '/auth/login')
  }
  const loginLabel = intl.formatMessage({
    id: isAdmission ? 'ADMISSION.DASH.CONTINUE_MY_APPLICATION' : 'VERIFICATION.GO_TO_LOGIN',
  })

  return (
    <Modal
      show={true}
      onHide={() => {}}
      aria-labelledby='contained-modal-title-vcenter'
      centered
      backdrop='static'
      keyboard={false}
    >
      <Modal.Body>
        <div className='d-flex flex-column align-items-center p-4'>
          <div className='text-center mb-4'>
            <Image
              src='/media/icons/duotune/general/authentication.png'
              alt='Verification'
              className='mb-2'
              style={{height: '100px'}}
            />

            {status === 'loading' && (
              <>
                <div className='spinner-border text-primary mb-4 mt-4' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <h1 className='text-dark mb-3 fw-bold fs-2x'>
                  {intl.formatMessage({id: 'VERIFICATION.VERIFYING'})}
                </h1>
                <p className='text-muted fw-semibold'>
                  {intl.formatMessage({
                    id: isAdmission ? 'ADMISSION.VERIFY.CHECKING' : 'VERIFICATION.PLEASE_WAIT',
                  })}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className='mb-4 mt-4'>
                  <i className='fas fa-check-circle text-success' style={{fontSize: '64px'}}></i>
                </div>
                <h1 className='text-dark mb-3 fw-bold fs-2x'>
                  {intl.formatMessage({
                    id: isAdmission ? 'ADMISSION.VERIFY.DONE_TITLE' : 'VERIFICATION.SUCCESS_TITLE',
                  })}
                </h1>
                <p className='text-muted fw-semibold mb-6'>{message}</p>
                <Button
                  variant='primary'
                  size='lg'
                  className='w-100 mt-4'
                  onClick={handleLoginClick}
                >
                  {loginLabel}
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className='mb-4 mt-4'>
                  <i
                    className='fas fa-exclamation-circle text-danger'
                    style={{fontSize: '64px'}}
                  ></i>
                </div>
                <h1 className='text-dark mb-3 fw-bold fs-2x'>
                  {intl.formatMessage({id: 'VERIFICATION.ERROR_TITLE'})}
                </h1>
                <p className='text-muted fw-semibold mb-4'>{message}</p>
                <div
                  className='alert alert-warning d-flex align-items-center p-4 mb-6'
                  role='alert'
                >
                  <i className='fas fa-info-circle me-3 fs-3'></i>
                  <div className='text-start'>
                    <small className='fw-semibold'>
                      {intl.formatMessage({id: 'VERIFICATION.CONTACT_SCHOOL'})}
                    </small>
                  </div>
                </div>
                <Button variant='primary' size='lg' className='w-100' onClick={handleLoginClick}>
                  {loginLabel}
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default VerifyEmailPage
